import { useCallback, useEffect, useMemo, useState } from "react";
import { listServiceResourceNotes } from "@/lib/service/client";
import { isServiceResourceNoteActive } from "@/lib/service/resourceNotesUtils";
import type { ServiceResourceNote, ServiceRig, ServiceTech } from "@/lib/service/types";

export type ServiceResourceAlertRow = {
  note: ServiceResourceNote;
  resourceLabel: string;
};

export function useServiceResourceNotesSummary(navTick: number, rigs: ServiceRig[], techs: ServiceTech[]) {
  const [notes, setNotes] = useState<ServiceResourceNote[]>([]);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setBusy(true);
    const res = await listServiceResourceNotes();
    setBusy(false);
    if (res.ok) setNotes(res.data.notes);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  const activeAlerts = useMemo((): ServiceResourceAlertRow[] => {
    const rigById = new Map(rigs.map((r) => [r.id, r.designation]));
    const techById = new Map(techs.map((t) => [t.id, t.display_name]));
    return notes
      .filter((n) => isServiceResourceNoteActive(n))
      .map((note) => ({
        note,
        resourceLabel:
          note.resource_type === "rig"
            ? (rigById.get(note.resource_id) ?? "Rig")
            : (techById.get(note.resource_id) ?? "Technician"),
      }));
  }, [notes, rigs, techs]);

  return { activeAlerts, alertCount: activeAlerts.length, busy, reload };
}
